/**
 * DashboardShared.tsx — shared dashboard atoms
 * P1 design: warm-neutral bordered cards (shadows retired), sage accent,
 * inline trend metrics. All values flow from src/theme/tokens.ts.
 */

import { ArrowUp, ArrowDown } from 'lucide-react'
import { attention, avatarPalette, ink, statusRun, surface } from '../../theme/tokens'
import { stageDotFor } from './stageDotFor'

// ── ClosrCard ─────────────────────────────────────────────────────────────────

export function ClosrCard({ children, className = '' }: { children: React.ReactNode; className?: string }) {
  return (
    <div className={`closr-card ${className}`}>
      {children}
    </div>
  )
}

// ── CardHeader ────────────────────────────────────────────────────────────────

export function CardHeader({ title, action, subtitle }: { title: string; action?: React.ReactNode; subtitle?: string }) {
  return (
    <div className="flex items-start justify-between px-5 py-4 border-b border-edge-row">
      <div>
        <div className="text-14 font-semibold text-ink-primary">{title}</div>
        {subtitle && <div className="text-12 text-ink-muted mt-1">{subtitle}</div>}
      </div>
      {action && <div className="flex items-center gap-2">{action}</div>}
    </div>
  )
}

// ── MetricCard (Closr style: icon + number + label + inline trend) ────────────

interface MetricCardProps {
  label: string
  value: string | number
  prevValue?: string | number
  trend?: number
  trendLabel?: string
  emoji?: string
  subline?: string             // context line under the value (carries honest empty-state copy)
  tone?: 'default' | 'amber'   // 'amber' = attention tint (finance overdue/attention)
}

export function MetricCard({ label, value, prevValue, trend, trendLabel, emoji, subline, tone = 'default' }: MetricCardProps) {
  const isUp = trend !== undefined && trend > 0
  const isDown = trend !== undefined && trend < 0
  const amber = tone === 'amber'
  return (
    <div
      className="closr-card p-4 flex flex-col gap-2"
      style={amber ? { background: attention['banner-bg'], borderColor: attention['banner-border'] } : undefined}
    >
      <div className="flex items-center gap-2">
        {emoji && (
          <span className="text-20" style={{ filter: 'none' }}>{emoji}</span>
        )}
        <span className="text-12 text-ink-secondary">{label}</span>
      </div>
      <div className="metric-num">{value}</div>
      {subline && <div className="text-11 text-ink-muted">{subline}</div>}
      {(trend !== undefined || prevValue !== undefined) && (
        <div className="flex items-center gap-1 text-11">
          {trend !== undefined && (
            <span className={`flex items-center gap-1 font-medium ${isUp ? 'trend-up' : isDown ? 'trend-down' : 'trend-neutral'}`}>
              {isUp ? <ArrowUp size={10} /> : isDown ? <ArrowDown size={10} /> : null}
              {Math.abs(trend ?? 0)}
            </span>
          )}
          {prevValue !== undefined && (
            <span className="text-ink-muted">Previously: {prevValue}</span>
          )}
          {trendLabel && <span className="text-ink-muted">{trendLabel}</span>}
        </div>
      )}
    </div>
  )
}

// ── SectionTitle ──────────────────────────────────────────────────────────────

export function SectionTitle({ title, subtitle, action }: { title: string; subtitle?: string; action?: React.ReactNode }) {
  return (
    <div className="flex items-start justify-between mb-5">
      <div>
        <h1 className="text-section-title font-semibold text-ink-primary leading-tight">{title}</h1>
        {subtitle && <p className="text-13 text-ink-secondary mt-1">{subtitle}</p>}
      </div>
      {action && <div>{action}</div>}
    </div>
  )
}

// ── TabStrip ──────────────────────────────────────────────────────────────────

/**
 * The platform's ONE in-page tab pattern, lifted verbatim from the Settings
 * page (2.2) when Reports gained a Forecast tab (2.3b, U-1): a role="tablist"
 * of plain role="tab" buttons with aria-selected, focusable in DOM order and
 * activated by Enter/Space — the button's own keyboard behaviour, no
 * roving-tabindex layer. Markup and classes are byte-identical to the strip
 * SettingsPage rendered inline before, so its pinned baselines hold, and a
 * second page can no longer invent a second pattern. The active tab is the
 * caller's state: one route, no router.
 */
export function TabStrip<T extends string>({ label, tabs, active, onSelect }: {
  label: string
  tabs: readonly { id: T; label: string }[]
  active: T
  onSelect: (id: T) => void
}) {
  return (
    <div role="tablist" aria-label={label} className="flex items-center gap-2 border-b border-edge-row pb-2">
      {tabs.map(t => (
        <button key={t.id} type="button" role="tab" aria-selected={active === t.id}
          className={`k-btn-quiet ${active === t.id ? 'text-ink-primary font-semibold' : 'text-ink-muted'}`}
          onClick={() => onSelect(t.id)}>{t.label}</button>
      ))}
    </div>
  )
}

// ── DealStageBadge ────────────────────────────────────────────────────────────
// Home tabs H-1b (RULED 2026-10-18): ONE stage colour everywhere — the board's
// label binding, stageDot, is the badge's source too. The pill is neutral
// chrome (the panel surface, body ink) and the STAGE is the dot — the board's
// own idiom, "a stage dot + name" (spec §3.4). An unknown stage gets the
// neutral dot, never a borrowed real-stage colour. Until H-1b the badge read a
// third definition (the `stage` bg/text palette through `.stage-*` classes),
// which agreed with neither the board nor the Home donut. The lookup itself
// is stageDotFor.ts — shared with funnelRows, and kept out of this file so it
// exports components only.

export function DealStageBadge({ stage }: { stage: string }) {
  return (
    <span className="stage-badge" style={{ background: surface.panel, color: ink.body }}>
      <span className="stage-badge-dot" style={{ background: stageDotFor(stage) }} aria-hidden="true" />
      {stage}
    </span>
  )
}

// ── StatusBadge (run status) ──────────────────────────────────────────────────

const STATUS_MAP: Record<string, { label: string; bg: string; color: string; dot?: boolean }> = {
  running:   { label: 'Running',   bg: statusRun.running.bg,   color: statusRun.running.text, dot: true },
  succeeded: { label: 'Succeeded', bg: statusRun.succeeded.bg, color: statusRun.succeeded.text },
  failed:    { label: 'Failed',    bg: statusRun.failed.bg,    color: statusRun.failed.text },
  waiting:   { label: 'Waiting',   bg: statusRun.waiting.bg,   color: statusRun.waiting.text, dot: true },
  queued:    { label: 'Queued',    bg: statusRun.queued.bg,    color: statusRun.queued.text },
  held:      { label: 'Held',      bg: statusRun.waiting.bg,   color: statusRun.waiting.text },
  active:    { label: 'Active',    bg: statusRun.active.bg,    color: statusRun.active.text },
  paused:    { label: 'Paused',    bg: statusRun.paused.bg,    color: statusRun.paused.text },
  archived:  { label: 'Archived',  bg: statusRun.archived.bg,  color: statusRun.archived.text },
  disabled:  { label: 'Disabled',  bg: statusRun.archived.bg,  color: statusRun.archived.text },
  healthy:   { label: 'Healthy',   bg: statusRun.healthy.bg,   color: statusRun.healthy.text },
}

export function StatusBadge({ status }: { status: string }) {
  const cfg = STATUS_MAP[status] ?? { label: status, bg: surface.panel, color: ink.secondary }
  return (
    <span
      className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-11 font-medium"
      style={{ background: cfg.bg, color: cfg.color }}
    >
      {cfg.dot && <span className="pulse-dot" style={{ background: cfg.color }} />}
      {cfg.label}
    </span>
  )
}

// ── DashButton ────────────────────────────────────────────────────────────────

export function DashButton({ children, onClick, variant = 'default', size = 'sm' }: {
  children: React.ReactNode
  onClick?: () => void
  variant?: 'default' | 'outline' | 'ghost'
  size?: 'sm' | 'md'
}) {
  const base = 'inline-flex items-center gap-2 font-medium rounded-control border transition-colors duration-100 cursor-pointer'
  const sizes = { sm: 'px-3 py-2 text-12', md: 'px-4 py-2 text-13' }
  const variants = {
    default: 'bg-accent text-ink-on-accent border-accent hover:bg-accent-hover',
    outline: 'bg-surface-control text-ink-body border-edge-control hover:bg-surface-row-hover',
    ghost:   'bg-transparent text-ink-secondary border-transparent hover:bg-surface-nav-hover',
  }
  return (
    <button className={`${base} ${sizes[size]} ${variants[variant]}`} onClick={onClick}>
      {children}
    </button>
  )
}

// ── Toggle ────────────────────────────────────────────────────────────────────

export function Toggle({ checked, onChange }: { checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <button
      role="switch"
      aria-checked={checked}
      onClick={() => onChange(!checked)}
      className={`relative inline-flex h-5 w-9 flex-shrink-0 rounded-full border-2 border-transparent transition-colors duration-200 ${checked ? 'bg-accent' : 'bg-ink-disabled'}`}
    >
      <span className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-surface-control transition duration-200 ${checked ? 'translate-x-4' : 'translate-x-0'}`} />
    </button>
  )
}

// ── EmptyState ────────────────────────────────────────────────────────────────

export function EmptyState({ message = 'No data available', hint, actionLabel, onAction }: {
  message?: string
  hint?: string
  actionLabel?: string
  onAction?: () => void
}) {
  // S-Tier (slice 2). klaxiora.css ships NO generic empty-state class; the
  // bundle's only empty idiom is pipeline.css's .k-lane-empty — a dashed tile
  // on --text3 with a quiet text action instead of a filled button. What is
  // adopted here is that SHAPE: dashed box, faint ink, quiet action, replacing
  // the old dashed circle + filled control button.
  //
  // Radius is --r-tile (the nested-tile radius, matching .k-lane-empty) via
  // the foundation's radius.tile export — the gap-fix that closed slice 2's
  // reported departure. One deliberate departure remains: the action is
  // .k-btn-quiet, the contract's own quiet button, NOT pipeline.css's
  // mono/accent text action, which klaxiora.css does not define.
  //
  // The outer py-16 rhythm is KEPT on purpose: PIPELINE.md calls for a compact
  // *lane* tile, and generalising that to every full-panel empty would reflow
  // all 29 consumers' page heights — well past what a lane rule authorises.
  return (
    <div className="flex flex-col items-center justify-center py-16 text-center gap-[7px]">
      <div className="flex flex-col items-center justify-center gap-[7px] px-[14px] py-[22px]
                      border border-dashed border-edge-control rounded-tile text-ink-muted">
        <div className="text-13 font-semibold text-ink-primary">{message}</div>
        {hint && <p className="text-12 text-ink-muted max-w-xs">{hint}</p>}
        {actionLabel && onAction && (
          <button onClick={onAction} className="k-btn-quiet">{actionLabel}</button>
        )}
      </div>
    </div>
  )
}

// ── Loading skeleton ────────────────────────────────────────────────────────────

export function LoadingState({ rows = 5 }: { rows?: number }) {
  // S-Tier (slice 2). The bundle defines NO skeleton treatment at all
  // (ADOPTION.md §4 lists it as an addition the bundle demands and lacks).
  // The foundation gap-fix added .k-skeleton (klaxiora.css additions +
  // ADOPTION.md §7) — same fill and pulse as these rows, tile radius. These
  // rows deliberately stay on `bg-track animate-pulse rounded-control` for
  // now: swapping is a department-slice restyle (a visible 10px→14px radius
  // step), not a foundation change. The wrapper is .k-card — identical in
  // resolved CSS to .closr-card since the foundation's rekey, but it names
  // the contract.
  return (
    <div className="page-enter flex flex-col gap-3">
      <div className="h-6 w-48 rounded-control bg-track-strong animate-pulse" />
      <div className="k-card p-5 flex flex-col gap-3">
        {Array.from({ length: rows }).map((_, i) => (
          <div key={i} className="h-9 w-full rounded-control bg-track animate-pulse" />
        ))}
      </div>
    </div>
  )
}

// ── Error state ───────────────────────────────────────────────────────────────

export function ErrorState({ message, onRetry }: { message: string; onRetry?: () => void }) {
  return (
    <div className="flex flex-col items-center justify-center py-16 text-center gap-3">
      <div className="text-3xl opacity-40" aria-hidden="true">⚠</div>
      <p className="text-13 text-danger-text">{message}</p>
      {onRetry && (
        <button onClick={onRetry} className="text-12 font-medium text-accent hover:underline">
          Retry
        </button>
      )}
    </div>
  )
}

// ── ComingSoon (module not yet built — e.g. HR / Finance) ──────────────────────

export function ComingSoon({ module, description }: { module: string; description?: string }) {
  return (
    <div className="page-enter flex flex-col items-center justify-center py-24 text-center gap-3">
      <div className="text-5xl opacity-25" aria-hidden="true">◷</div>
      <h2 className="text-section-title font-semibold text-ink-primary m-0">{module} module coming soon</h2>
      <p className="text-13 text-ink-secondary max-w-[420px] leading-relaxed m-0">
        {description ?? `The ${module} module is not yet connected. Once its backend is live, this view will populate with real data automatically.`}
      </p>
    </div>
  )
}

// (UploadButton retired in S-Tier slice 6: HR and Finance both localised
// their k-btn file-pickers — UploadInvoiceButton / UploadCVButton — and no
// consumer remained.)

// ── Avatar ────────────────────────────────────────────────────────────────────

export function Avatar({ name, size = 28 }: { name: string; size?: number }) {
  const initials = name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()
  const color = avatarPalette[name.charCodeAt(0) % avatarPalette.length]
  return (
    <div
      className="rounded-full flex items-center justify-center flex-shrink-0 text-ink-on-accent font-semibold"
      style={{ width: size, height: size, background: color, fontSize: size * 0.38 }}
    >
      {initials}
    </div>
  )
}
